import React from 'react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthContext, useAuthProvider } from './hooks/useAuth';
import { BackofficeAuthContext, useBackofficeAuthProvider } from './hooks/useBackofficeAuth';
import { CartContext, useCartProvider } from './hooks/useCart';
import { ApiCartProvider } from './hooks/useApiCartProvider';
import { router } from './routes';
import { theme } from './config/theme';
import { InstallPromptBanner } from './components/pwa/InstallPromptBanner';
import { SWUpdateNotification } from './components/pwa/SWUpdateNotification';
import { OfflineBanner } from './components/pwa/OfflineBanner';
import { ChatWidget } from './components/ChatWidget';
import { initAnalytics, trackPageView } from './services/analytics';
import './i18n';

// Initialise GA4 at module load. Safe on non-prod envs -- the
// helper no-ops when REACT_APP_GOOGLE_ANALYTICS_ID isn't set or
// REACT_APP_ENVIRONMENT !== 'production'.
initAnalytics();

// Record the initial landing page view. Subsequent navigation
// under CustomerLayout is covered by <RouteTracker> which fires on
// every route change. This one-shot ensures auth pages
// (/login, /register, /activate, /forgot-password) that live
// outside CustomerLayout still register a page view when the user
// lands directly on them.
if (typeof window !== 'undefined') {
  trackPageView(window.location.pathname + window.location.search);
}

function App() {
  const authValue = useAuthProvider();
  const backofficeAuthValue = useBackofficeAuthProvider();
  const cartValue = useCartProvider();

  // Register service worker for PWA functionality
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContext.Provider value={authValue}>
        <BackofficeAuthContext.Provider value={backofficeAuthValue}>
          <CartContext.Provider value={cartValue}>
            <ApiCartProvider>
              <RouterProvider router={router} />
            </ApiCartProvider>
          </CartContext.Provider>
        </BackofficeAuthContext.Provider>
      </AuthContext.Provider>
      <InstallPromptBanner />
      <SWUpdateNotification />
      <OfflineBanner />
      <ChatWidget />
    </ThemeProvider>
  );
}

export default App;
